import {
  expectDefined,
  fixedNumber,
  isClose,
  lerp,
  lerpMap,
  newUuid,
  NumberBooleanSchema
} from '@utils';
import { z } from 'zod';
import { Angle, AngleDegreesSchema } from './Angle';
import { bezier, BezierCurve } from './BezierCurve';
import { lineSegment } from './constructors';
import { Ellipse } from './Ellipse';
import { svgPathDataFromEndpointsEllipse } from './ellipseParameterizations';
import { SERIALIZATION_PRECISION } from './GeomConstants';
import type { IPath, ParamPoint } from './IPath';
import { LineSegment } from './LineSegment';
import { Point, PointSchema } from './Point';
import type { PolyLine } from './PolyLine';
import type { Rect } from './Rect';
import { tokenizeSvgPathData } from './svgUtils';

export const PathSchema = z.unknown().transform((val, ctx) => {
  try {
    if (typeof val === 'string') {
      return Path.fromSvgPathData(val);
    } else {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: `Path must be a string, but received ${typeof val}`
      });
      return z.NEVER;
    }
  } catch (err) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: (err as Error).message
    });
    return z.NEVER;
  }
});

export function path(segments?: IPath[]): PathBuilder {
  const builder = new PathBuilder();
  if (segments) {
    builder.addSegments(segments);
  }
  return builder;
}

export class PathBuilder {
  segments: IPath[] = [];

  build(): Path {
    return new Path(this.segments);
  }

  get isEmpty(): boolean {
    return this.segments.length === 0;
  }

  get start(): Point {
    if (this.isEmpty) {
      throw new Error('Cannot get start point of an empty path');
    }
    return this.segments[0].start;
  }

  get end(): Point {
    if (this.isEmpty) {
      throw new Error('Cannot get end point of an empty path');
    }
    return this.segments[this.segments.length - 1].end;
  }

  segmentAtIndex(index: number): IPath {
    if (index < 0 || index >= this.segments.length) {
      throw new Error('Segment index out of range');
    }
    return this.segments[index];
  }

  get lastSegment(): IPath {
    return this.segmentAtIndex(this.segments.length - 1);
  }

  get isLastSegmentPoint(): boolean {
    return !this.isEmpty && this.lastSegment instanceof Point;
  }

  addSegment(segment: IPath): this {
    this.segments.push(segment);
    return this;
  }

  moveTo(p: Point): this {
    if (this.isLastSegmentPoint) {
      this.segments.pop();
    }
    this.addSegment(p);
    return this;
  }

  lineTo(p: Point): this {
    if (this.isEmpty) {
      throw new Error('Path is empty');
    }
    let end = this.end;
    if (this.isLastSegmentPoint) {
      end = this.segments.pop() as Point;
    }
    this.addSegment(lineSegment(end, p));
    return this;
  }

  curveTo(cp1: Point, cp2: Point, end: Point): this {
    if (this.isEmpty) {
      throw new Error('Path is empty');
    }
    let start = this.end;
    if (this.isLastSegmentPoint) {
      start = this.segments.pop() as Point;
    }
    this.addSegment(bezier([start, cp1, cp2, end]));
    return this;
  }

  arcTo(
    radii: Point,
    xRotation: Angle,
    largeArc: boolean,
    sweep: boolean,
    end: Point
  ): this {
    if (this.isEmpty) {
      throw new Error('Path is empty');
    }
    let start = this.end;
    if (this.isLastSegmentPoint) {
      start = this.segments.pop() as Point;
    }
    const ellipse = Ellipse.fromEndpointsEllipse({
      start,
      end,
      radii,
      xRotation,
      largeArc,
      sweep
    });
    this.addSegment(ellipse);
    return this;
  }

  addSegments(segments: IPath[]): this {
    this.segments.push(...segments);
    return this;
  }
}

export class Path implements IPath {
  readonly #id = newUuid();
  readonly #segments: IPath[];

  #cache:
    | {
        length?: number;
        bounds?: Rect;
      }
    | undefined;

  // #region Constructors

  constructor(segments?: IPath[]) {
    this.#segments = segments ?? [];
  }

  static fromSegments(segments: IPath[]): Path {
    return new Path(segments);
  }

  static fromLineSegment(line: LineSegment): Path {
    return Path.fromSegments([line]);
  }

  static fromPolyLine(polyLine: PolyLine): Path {
    const builder = path();
    for (let i = 0; i < polyLine.points.length - 1; i++) {
      builder.addSegment(lineSegment(polyLine.points[i], polyLine.points[i + 1]));
    }
    return builder.build();
  }

  static fromPoints(points: Point[]): Path {
    const builder = path();
    for (let i = 0; i < points.length - 1; i++) {
      builder.addSegment(lineSegment(points[i], points[i + 1]));
    }
    return builder.build();
  }

  // #endregion

  recalculateCache() {
    if (!this.isEmpty) {
      this.#cache = {
        length: this._length,
        bounds: this._bounds
      };
      for (const segment of this.segments) {
        segment.recalculateCache();
      }
    } else {
      this.#cache = undefined;
    }
  }

  // #region Attributes

  get id(): string {
    return this.#id;
  }
  get segments(): IPath[] {
    return this.#segments;
  }

  get isEmpty(): boolean {
    return this.segments.length === 0;
  }

  get start(): Point {
    if (this.isEmpty) {
      throw new Error('Cannot get start point of an empty path');
    }
    return this.segments[0].start;
  }

  get end(): Point {
    if (this.isEmpty) {
      throw new Error('Cannot get end point of an empty path');
    }
    return this.segments[this.segments.length - 1].end;
  }

  private get _length(): number {
    if (this.isEmpty) {
      return 0;
    }

    let totalLength = 0;
    for (const segment of this.segments) {
      totalLength += segment.length;
    }

    return totalLength;
  }

  get length(): number {
    if (this.isEmpty) {
      return 0;
    }
    if (this.#cache?.length === undefined) {
      const length = this._length;
      this.#cache = { ...(this.#cache ?? {}), length };
    }
    return expectDefined(this.#cache.length);
  }

  private get _bounds(): Rect {
    if (this.isEmpty) {
      throw new Error('Cannot get bounds of an empty path');
    }
    let bounds = this.segments[0].bounds;
    for (let i = 1; i < this.segments.length; i++) {
      bounds = bounds.union(this.segments[i].bounds);
    }
    return bounds;
  }

  get bounds(): Rect {
    if (this.isEmpty) {
      throw new Error('Cannot get bounds of an empty path');
    }
    if (this.#cache?.bounds === undefined) {
      const bounds = this._bounds;
      this.#cache = { ...(this.#cache ?? {}), bounds };
    }
    return expectDefined(this.#cache.bounds);
  }

  segmentAtIndex(index: number): IPath {
    if (index < 0 || index >= this.segments.length) {
      throw new Error('Segment index out of range');
    }
    return this.segments[index];
  }

  get firstSegment(): IPath {
    return this.segmentAtIndex(0);
  }

  get lastSegment(): IPath {
    return this.segmentAtIndex(this.segments.length - 1);
  }

  get isLastSegmentPoint(): boolean {
    return !this.isEmpty && this.lastSegment instanceof Point;
  }

  // #endregion

  // #region Comparison

  equals(other: Path): boolean {
    if (this === other) {
      return true;
    }
    if (this.segments.length !== other.segments.length) {
      return false;
    }
    for (let i = 0; i < this.segments.length; i++) {
      if (!this.segments[i].isSamePath(other.segments[i])) {
        return false;
      }
    }
    return true;
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  isSamePath(other: IPath): boolean {
    throw new Error(
      'Method not implemented for Path, only path segments. Use equals() instead.'
    );
  }

  isIsomorphicTo(other: IPath): boolean {
    if (!(other instanceof Path)) {
      return false;
    }
    if (this === other) {
      return true;
    }
    if (this.segments.length !== other.segments.length) {
      return false;
    }
    for (let i = 0; i < this.segments.length; i++) {
      if (!this.segments[i].isIsomorphicTo(other.segments[i])) {
        return false;
      }
    }
    return true;
  }

  // #endregion

  // #region Interpolation

  private _lerp(t: number, end: Path): Path {
    const builder = path();
    for (let i = 0; i < this.segments.length; i++) {
      builder.addSegment(this.segments[i].lerp(t, end.segments[i]));
    }
    return builder.build();
  }

  lerp(t: number, end: this): this {
    if (!this.isIsomorphicTo(end)) {
      throw new Error('Paths must have same segment types and structure for lerp.');
    }
    return this._lerp(t, end) as this;
  }

  interp(end: this): (t: number) => this {
    if (!this.isIsomorphicTo(end)) {
      throw new Error(
        'Paths must have same segment types and structure for interpolation.'
      );
    }
    return (t: number) => this._lerp(t, end) as this;
  }

  // #endregion

  // #region Geometric Operations

  reversed(): this {
    const builder = path();
    for (let i = this.segments.length - 1; i >= 0; i--) {
      builder.addSegment(this.segments[i].reversed());
    }
    return builder.build() as this;
  }

  flattened(flatness: number): ParamPoint[] {
    const result: ParamPoint[] = [{ t: 0, p: this.start }];

    const segments = this.paramSegments;
    for (let j = 0; j < segments.length; j++) {
      const { t: segStartT, segment } = segments[j];
      const points = segment.flattened(flatness);
      const segEndT = j < segments.length - 1 ? segments[j + 1].t : 1;
      // Add all but the first point of the segment, since the first point is
      // already added in the previous segment.
      for (let i = 1; i < points.length; i++) {
        const { t, p } = points[i];
        result.push({
          t: lerp(t, [segStartT, segEndT]),
          p
        });
      }
    }

    return result;
  }

  segmentAt(t: number): {
    segment: IPath;
    segT: number;
    lengthOfPreviousSegments: number;
  } {
    if (this.isEmpty) {
      throw new Error('Cannot get segment of empty path');
    }

    const totalLength = this.length;

    if (t <= 0) {
      return {
        segment: this.firstSegment,
        segT: 0,
        lengthOfPreviousSegments: 0
      };
    }

    if (t >= 1) {
      const lastSegment = this.lastSegment;
      return {
        segment: lastSegment,
        segT: 1,
        lengthOfPreviousSegments: totalLength - lastSegment.length
      };
    }

    let targetDistance = t * totalLength;
    let lengthOfPreviousSegments = 0;

    for (const segment of this.segments) {
      const segmentLength = segment.length;
      if (targetDistance <= segmentLength) {
        return {
          segment,
          segT: targetDistance / segmentLength,
          lengthOfPreviousSegments
        };
      }

      targetDistance -= segmentLength;
      lengthOfPreviousSegments += segmentLength;
    }

    // Should not reach here
    throw new Error('Invalid Path segment');
  }

  get paramSegments(): { t: number; segment: IPath }[] {
    if (this.isEmpty) {
      return [];
    }
    const result = [];
    let accumulatedLength = 0;
    for (const segment of this.segments) {
      result.push({
        t: accumulatedLength,
        segment
      });
      accumulatedLength += segment.length;
    }
    if (accumulatedLength > 0) {
      // Check for single point path
      for (const r of result) {
        r.t /= accumulatedLength;
      }
    }
    return result;
  }

  lengthAt(t: number): number {
    const { lengthOfPreviousSegments, segment, segT } = this.segmentAt(t);
    return lengthOfPreviousSegments + segment.length * segT;
  }

  pointAt(t: number): Point {
    const { segment, segT } = this.segmentAt(t);
    return segment.pointAt(segT);
  }

  derivative(t: number): Point {
    const { segment, segT } = this.segmentAt(t);
    return segment.derivative(segT);
  }

  normal(t: number): Point {
    const { segment, segT } = this.segmentAt(t);
    return segment.normal(segT);
  }

  angle(t: number): Angle {
    const { segment, segT } = this.segmentAt(t);
    return segment.angle(segT);
  }

  project(p: Point): { p: Point; t: number; d: number } {
    if (this.isEmpty) {
      throw new Error('Cannot project onto empty Path');
    }

    let closestPoint: Point | undefined;
    let closestDistance = Infinity;
    let overallT = 0;
    let accumulatedLength = 0;
    const totalLength = this.length;

    for (const segment of this.segments) {
      const projection = segment.project(p);
      const distance = projection.d;
      if (distance < closestDistance) {
        closestDistance = distance;
        closestPoint = projection.p;
        overallT = (accumulatedLength + projection.t * segment.length) / totalLength;
      }
      accumulatedLength += segment.length;
    }

    return { p: expectDefined(closestPoint), t: overallT, d: closestDistance };
  }

  /**
   * Determines whether this path intersects the given rectangle.
   *
   * @param r The rectangle.
   *
   * @returns True if the path intersects the rectangle, false otherwise.
   */
  intersectsRect(r: Rect): boolean {
    if (this.isEmpty) {
      return false;
    }
    for (const segment of this.segments) {
      if (segment.intersectsRect(r)) {
        return true;
      }
    }
    return false;
  }

  // #endregion

  // #region Serialization

  toJSON(): unknown {
    return this.toSvgPathData(SERIALIZATION_PRECISION);
  }

  static fromJSON(json: unknown): Path {
    return PathSchema.parse(json);
  }

  // #endregion

  // #region SVG Conversion

  toSvgPathData(fractionDigits?: number): string {
    if (this.isEmpty) {
      return '';
    }
    let d = `M${fixedNumber(this.start.x, fractionDigits)} ${fixedNumber(this.start.y, fractionDigits)}`;
    for (const segment of this.segments) {
      if (segment instanceof LineSegment) {
        d += `L${fixedNumber(segment.end.x, fractionDigits)} ${fixedNumber(segment.end.y, fractionDigits)}`;
      } else if (segment instanceof BezierCurve) {
        d += `C${fixedNumber(segment.points[1].x, fractionDigits)} ${fixedNumber(segment.points[1].y, fractionDigits)} ${fixedNumber(segment.points[2].x, fractionDigits)} ${fixedNumber(segment.points[2].y, fractionDigits)} ${fixedNumber(segment.end.x, fractionDigits)} ${fixedNumber(segment.end.y, fractionDigits)}`;
      } else if (segment instanceof Ellipse) {
        d += svgPathDataFromEndpointsEllipse(segment, fractionDigits, false);
      }
    }
    return d;
  }

  static fromSvgPathData(d: string): Path {
    const builder = path();
    const tokens = tokenizeSvgPathData(d);
    let lastPoint = Point.ZERO;

    let i = 0;
    while (i < tokens.length) {
      const cmd = tokens[i++] as string; // Command is a string
      switch (cmd) {
        case 'M': {
          if (i + 1 >= tokens.length)
            throw new Error(`'M' path command requires 2 parameters, [x, y]`);
          lastPoint = PointSchema.parse([tokens[i++], tokens[i++]]);
          builder.moveTo(lastPoint);
          break;
        }
        case 'L': {
          if (i + 1 >= tokens.length)
            throw new Error(`'L' path command requires 2 parameters, [x, y]`);
          const endPoint = PointSchema.parse([tokens[i++], tokens[i++]]);
          builder.lineTo(endPoint);
          break;
        }
        case 'C': {
          if (i + 5 >= tokens.length)
            throw new Error(
              `'C' path command requires 6 parameters, [x1, y1, x2, y2, x, y]`
            );
          const cp1 = PointSchema.parse([tokens[i++], tokens[i++]]);
          const cp2 = PointSchema.parse([tokens[i++], tokens[i++]]);
          const endPoint = PointSchema.parse([tokens[i++], tokens[i++]]);
          builder.curveTo(cp1, cp2, endPoint);
          break;
        }
        case 'A': {
          if (i + 6 >= tokens.length)
            throw new Error(
              `'A' path command requires 7 parameters, [rx, ry, xRotation, largeArc, sweep, x, y]`
            );
          const radii = PointSchema.parse([tokens[i++], tokens[i++]]);
          const xRotation = AngleDegreesSchema.parse(tokens[i++]);
          const largeArc = NumberBooleanSchema.parse(tokens[i++]);
          const sweep = NumberBooleanSchema.parse(tokens[i++]);
          const end = PointSchema.parse([tokens[i++], tokens[i++]]);
          builder.arcTo(radii, xRotation, largeArc, sweep, end);
          break;
        }
        default:
          throw new Error(`Unknown command: ${cmd}`);
      }
    }
    return builder.build();
  }

  // #endregion
}

// #region Free Functions

/**
 * Returns a polyline interpolation between two paths `a` and `b`.
 *
 * This function flattens both paths and then matches points from each path
 * based on their parameter values (`t`). If the parameter values are close
 * enough, the points are paired together. If not, the function finds a point on
 * the other path that corresponds to the lower parameter value, advancing
 * through the paths until the output pairs contain a point at every `t` in
 * both paths.
 *
 * The resulting set of point pairs will have at least elements as the flattened
 * path with the greatest number of points.
 *
 * The result can be used to visually interpolate between the two paths at a
 * given parameter value.
 *
 * @param a - The first path to interpolate.
 * @param b - The second path to interpolate.
 * @param flatness - The tolerance used to flatten the paths.
 * @returns An array of point pairs, where each pair consists of a point from
 *          path `a` and a corresponding point from path `b`.
 */
export function lerpPaths(a: Path, b: Path, flatness: number): [number, Point, Point][] {
  const aFlat = a.flattened(flatness);
  const bFlat = b.flattened(flatness);
  let aFlatIndex = 0;
  let bFlatIndex = 0;

  const aParamSegs = a.paramSegments;
  const bParamSegs = b.paramSegments;
  let aSegIndex = 0;
  let bSegIndex = 0;
  let aSeg = aParamSegs[aSegIndex];
  let bSeg = bParamSegs[bSegIndex];
  let aSegStart = aSeg.t;
  let aSegEnd = aParamSegs.length > 1 ? aParamSegs[1].t : 1;
  let bSegStart = bSeg.t;
  let bSegEnd = bParamSegs.length > 1 ? bParamSegs[1].t : 1;

  const pointPairs: [number, Point, Point][] = [];

  let currentT = 0;
  while (currentT < 1) {
    const aT = aFlat[aFlatIndex].t;
    const bT = bFlat[bFlatIndex].t;
    if (isClose(aT, bT)) {
      // The two t values are close enough to be considered the same
      const aPoint = aFlat[aFlatIndex++].p;
      const bPoint = bFlat[bFlatIndex++].p;
      currentT = Math.max(aT, bT);
      pointPairs.push([currentT, aPoint, bPoint]);
    } else if (aT < bT) {
      // The aT is earlier than bT
      const aPoint = aFlat[aFlatIndex++].p;

      // Use aT to find the point on path b
      while (aT > bSegEnd) {
        bSeg = bParamSegs[++bSegIndex];
        bSegStart = bSeg.t;
        bSegEnd = bParamSegs.length > bSegIndex + 1 ? bParamSegs[bSegIndex + 1].t : 1;
      }

      const bPoint = bSeg.segment.pointAt(lerpMap(aT, [bSegStart, bSegEnd], [0, 1]));

      currentT = aT;
      pointPairs.push([currentT, aPoint, bPoint]);
    } else {
      // The bT is earlier than aT
      const bPoint = bFlat[bFlatIndex++].p;

      // Use bT to find the point on path a
      while (bT > aSegEnd) {
        aSeg = aParamSegs[++aSegIndex];
        aSegStart = aSeg.t;
        aSegEnd = aParamSegs.length > aSegIndex + 1 ? aParamSegs[aSegIndex + 1].t : 1;
      }

      const aPoint = aSeg.segment.pointAt(lerpMap(bT, [aSegStart, aSegEnd], [0, 1]));

      currentT = bT;
      pointPairs.push([currentT, aPoint, bPoint]);
    }
  }

  return pointPairs;
}

export function interpPaths(a: Path, b: Path, flatness: number): (t: number) => Path {
  const pointPairs = lerpPaths(a, b, flatness);
  return (t: number) => {
    const points = [];
    for (const [, aPoint, bPoint] of pointPairs) {
      points.push(aPoint.lerp(t, bPoint));
    }
    return Path.fromPoints(points);
  };
}

// #endregion
